import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscribedPrograms } from "../../hooks/useSubscribedPrograms";
import { useAuth } from "../../contexts/AuthContext";
import { Icons } from "../SkillsAcademy/icons.jsx";
import ProgramCard from "./ProgramCard";
import PageTitle from "../PageTitle/PageTitle";
import ActionButton from "../ActionButton/ActionButton";
import type { Enrollment } from "../../contexts/FreeTrialContext";
import { DashLoader } from "../../components/DashLoader/DashLoader";
import {
  MyProgramsRoot,
  MyProgramsHeaderRow,
  MyProgramsStatsCard,
  MyProgramsStatBlock,
  MyProgramsStatNum,
  MyProgramsFilterRow,
  MyProgramsFilterChip,
  MyProgramsSectionBlock,
  MyProgramsSectionHeading,
  MyProgramsSectionSubtext,
  MyProgramsGrid,
  MyProgramsEmpty,
  MyProgramsEmptyTitle,
} from "./style.js";

// ── Filter ────────────────────────────────────────────────────────────────────

type FilterKey = "all" | "in-progress" | "free-trials" | "completed";

// ── Component ─────────────────────────────────────────────────────────────────

const MyPrograms = () => {
  const { data: enrollments = [], isLoading } = useSubscribedPrograms();
  const { loggedInUser } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>("all");

  const firstName: string =
    (loggedInUser as { first_name?: string } | undefined)?.first_name ??
    "there";

  const allEnrollments = useMemo<Enrollment[]>(
    () => enrollments,
    [enrollments],
  );

  // Stats always reflect totals, not the current filter
  const enrolledCount = allEnrollments.length;
  const inProgressCount = allEnrollments.filter((e) => !e.completed).length;
  const completedCount = allEnrollments.filter((e) => e.completed).length;
  const trialCount = allEnrollments.filter((e) => e.type === "trial").length;

  const FILTER_OPTIONS: { label: string; key: FilterKey; count: number }[] = [
    { label: "All", key: "all", count: enrolledCount },
    { label: "In progress", key: "in-progress", count: inProgressCount },
    { label: "Free trials", key: "free-trials", count: trialCount },
    { label: "Completed", key: "completed", count: completedCount },
  ];

  const filtered = useMemo<Enrollment[]>(() => {
    switch (filter) {
      case "in-progress":
        return allEnrollments.filter((e) => !e.completed);
      case "free-trials":
        return allEnrollments.filter((e) => e.type === "trial");
      case "completed":
        return allEnrollments.filter((e) => e.completed);
      default:
        return allEnrollments;
    }
  }, [allEnrollments, filter]);

  const inProgress = filtered.filter((e) => !e.completed);
  const completed = filtered.filter((e) => e.completed);

  const isEmpty = filtered.length === 0;

  if (isLoading) {
    return (
      <MyProgramsRoot>
        <DashLoader text="Loading your programs" />
      </MyProgramsRoot>
    );
  }

  return (
    <MyProgramsRoot>
      {/* ── Header ── */}
      <MyProgramsHeaderRow>
        <PageTitle topText="Your Learning" text="My Programs" noMargin>
          Welcome back,{" "}
          <strong style={{ color: "#FEFEFE" }}>{firstName}</strong>. Pick up
          where you left off, or finish a trial to unlock the full program.
        </PageTitle>

        <MyProgramsStatsCard>
          {[
            [enrolledCount, "enrolled"],
            [inProgressCount, "in progress"],
            [completedCount, "completed"],
          ].map(([num, label]) => (
            <MyProgramsStatBlock key={label as string}>
              <MyProgramsStatNum>{num}</MyProgramsStatNum>
              {label}
            </MyProgramsStatBlock>
          ))}
        </MyProgramsStatsCard>
      </MyProgramsHeaderRow>

      {/* ── Filters ── */}
      <MyProgramsFilterRow>
        {FILTER_OPTIONS.map(({ label, key, count }) => (
          <MyProgramsFilterChip
            key={key}
            type="button"
            $isSelected={filter === key}
            onClick={() => setFilter(key)}
          >
            {label}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background:
                  filter === key
                    ? "rgba(3,0,57,0.35)"
                    : "rgba(255,255,255,0.12)",
                fontSize: 10,
                fontWeight: 700,
                color: "#FEFEFE",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {count}
            </span>
          </MyProgramsFilterChip>
        ))}

        <ActionButton
          variant="subtle"
          size="sm"
          icon={<Icons.book size={15} />}
          type="button"
          onClick={() => navigate("/dashboard/skills-academy")}
          style={{ marginLeft: "auto" }}
        >
          Browse Skills Academy
        </ActionButton>
      </MyProgramsFilterRow>

      {/* ── Content ── */}
      {isEmpty ? (
        <MyProgramsEmpty>
          <Icons.book size={36} style={{ color: "#A87EFF" }} />
          <MyProgramsEmptyTitle>No programs here yet</MyProgramsEmptyTitle>
          <p style={{ margin: 0, fontSize: 14, color: "#A87EFF" }}>
            {filter === "free-trials"
              ? "Start a free trial from the Skills Academy to see programs here."
              : "Try a different filter or enrol in a program from the Skills Academy."}
          </p>
        </MyProgramsEmpty>
      ) : (
        <>
          {inProgress.length > 0 && (
            <MyProgramsSectionBlock>
              <MyProgramsSectionHeading>In progress</MyProgramsSectionHeading>
              <MyProgramsSectionSubtext>
                Pick up where you left off. Free trials and full programs side
                by side.
              </MyProgramsSectionSubtext>
              <MyProgramsGrid>
                {inProgress.map((enrollment) => (
                  <ProgramCard
                    key={enrollment.programId}
                    enrollment={enrollment}
                  />
                ))}
              </MyProgramsGrid>
            </MyProgramsSectionBlock>
          )}

          {completed.length > 0 && (
            <MyProgramsSectionBlock>
              <MyProgramsSectionHeading>Completed</MyProgramsSectionHeading>
              <MyProgramsSectionSubtext>
                Programs you've finished — view your results or revisit
                sessions.
              </MyProgramsSectionSubtext>
              <MyProgramsGrid>
                {completed.map((enrollment) => (
                  <ProgramCard
                    key={enrollment.programId}
                    enrollment={enrollment}
                  />
                ))}
              </MyProgramsGrid>
            </MyProgramsSectionBlock>
          )}
        </>
      )}
    </MyProgramsRoot>
  );
};

export default MyPrograms;
